import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNoKeyboardResize } from '../hooks/useNoKeyboardResize'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authApi, userApi } from '../api'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import { toastUtil } from '../utils/toast'
import { useAuthStore } from '../stores/authStore'

const ERROR_COLOR = 'rgba(180, 5, 8, 0.8)' // #B40508 80%
const SOLAPI_UNSUBSCRIBE_URL = 'https://console.solapi.com/unsubscribe'

type UnsubscribedNotice = { message: string; url: string }

// 솔라피 1061/2061(080 수신거부) 실패면 서버가 code=SMS_UNSUBSCRIBED + 해제 링크를 준다.
const getUnsubscribedNotice = (e: any): UnsubscribedNotice | null => {
  const data = e?.response?.data
  if (data?.code !== 'SMS_UNSUBSCRIBED') return null
  return {
    message: data.message || '인증 문자가 수신거부로 막혔어요',
    url: data.unsubscribe_url || SOLAPI_UNSUBSCRIBE_URL,
  }
}

/**
 * 문자(SMS) 인증번호 입력 화면.
 * 휴대폰 번호 입력(/register/phone) 다음 단계.
 * 진입 시 솔라피로 OTP 발송 → 6자리 입력 시 서버 검증 후 흐름 분기.
 */
const PhoneVerify: React.FC = () => {
  const navigate = useNavigate()
  useNoKeyboardResize()
  const [searchParams] = useSearchParams()
  const phone = searchParams.get('phone') || ''
  const mode = searchParams.get('mode') || '' // 'login' | 'signup'
  const socialRaw = searchParams.get('social') || ''
  const phoneFmt =
    phone.length === 11
      ? `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`
      : phone

  const { setToken, setUserId, getPostLoginPath } = useAuthStore()

  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [verifying, setVerifying] = useState(false)
  const isValid = code.length === 6

  // 재전송 쿨다운 타이머(초)
  const [cooldown, setCooldown] = useState(0)
  // 수신거부 번호 안내(해제 링크). 발송 성공하면 닫힌다.
  const [unsubscribed, setUnsubscribed] = useState<UnsubscribedNotice | null>(
    null
  )

  // 토큰 발급 응답을 받아 로그인 상태 저장 → 홈 진입까지 공통 처리.
  const completeLogin = useCallback(
    async (token: string) => {
      setToken(token)

      let contactConsent = false
      try {
        const profile = await userApi.getProfile()
        setUserId(profile.user.id)
        contactConsent = profile.user.contact_consent ?? false
      } catch {
        // 프로필 로드 실패는 무시하고 진행
      }

      navigate(getPostLoginPath(contactConsent), { replace: true })
    },
    [setToken, setUserId, getPostLoginPath, navigate]
  )

  // 기존 계정 OTP 로그인 (이메일 유저 폰번호 승계 — 가입계정 있으면 그 계정으로 로그인)
  const loginWithPhone = useCallback(async () => {
    try {
      const res = await authApi.loginOtp({ phone })
      if (!res.token) {
        setError(res.message || '로그인에 실패했습니다')
        return
      }
      await completeLogin(res.token)
    } catch (e: any) {
      const data = e?.response?.data
      if (data?.is_deleted) {
        toastUtil.error('탈퇴한 계정입니다')
        return
      }
      setError(data?.message || '로그인 중 오류가 발생했습니다')
    }
  }, [phone, completeLogin])

  // 소셜 + 기존번호 = 소셜을 기존계정에 연결 후 로그인
  const linkSocialAndLogin = useCallback(async () => {
    // social 파라미터(base64 { provider, provider_id }) 복호화
    let provider: any
    let providerId: string
    try {
      const parsed = JSON.parse(atob(socialRaw))
      provider = parsed.provider
      providerId = parsed.provider_id
    } catch {
      setError('소셜 정보가 올바르지 않습니다')
      return
    }

    try {
      const res = await authApi.linkSocialOtp({
        phone,
        provider,
        provider_id: providerId,
      })
      if (!res.token) {
        setError(res.message || '계정 연결에 실패했습니다')
        return
      }
      toastUtil.success('소셜 계정이 연결되었습니다')
      await completeLogin(res.token)
    } catch (e: any) {
      const data = e?.response?.data
      if (data?.is_deleted) {
        toastUtil.error('탈퇴한 계정입니다')
        return
      }
      setError(data?.message || '계정 연결 중 오류가 발생했습니다')
    }
  }, [socialRaw, phone, completeLogin])

  // OTP 검증 통과 후 흐름 분기.
  const proceedAfterVerified = useCallback(async () => {
    if (socialRaw) {
      // 소셜: 이 번호로 기존 계정이 있는지 확인
      try {
        const res = await authApi.checkPhone({ phone })
        if (res.available === false) {
          // 기존 번호 계정 → 소셜 연결 후 로그인
          await linkSocialAndLogin()
        } else {
          // 신규 번호 → 아이디/닉네임 입력으로
          navigate(`/signup?phone=${phone}&social=${socialRaw}`, { replace: true })
        }
      } catch {
        toastUtil.error('확인 중 오류가 발생했습니다')
      }
    } else if (mode === 'login') {
      // 기존 회원 OTP 로그인
      await loginWithPhone()
    } else {
      // 신규 번호 가입 → 아이디/닉네임 입력으로
      navigate(`/signup?phone=${phone}`, { replace: true })
    }
  }, [socialRaw, mode, phone, navigate, loginWithPhone, linkSocialAndLogin])

  // 서버에 인증번호 검증 요청 → 통과 시 분기
  const handleVerified = useCallback(async () => {
    if (verifying) return
    setVerifying(true)
    try {
      const res = await authApi.verifyOtp({ phone, code })
      if (res.verified) {
        await proceedAfterVerified()
      } else {
        setError(res.message || '인증번호가 일치하지 않습니다')
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.message || '인증번호가 일치하지 않습니다'
      )
    } finally {
      setVerifying(false)
    }
  }, [verifying, phone, code, proceedAfterVerified])

  // 진입 시 OTP 발송 (번호 있을 때 1회) + 쿨다운 시작
  useEffect(() => {
    if (!phone) return
    let alive = true
    authApi
      .sendOtp({ phone })
      .then(() => {
        if (alive) setCooldown(60)
      })
      .catch((e: any) => {
        if (!alive) return
        const notice = getUnsubscribedNotice(e)
        if (notice) {
          setUnsubscribed(notice)
          return
        }
        toastUtil.error(
          e?.response?.data?.message || '인증번호 발송에 실패했습니다'
        )
      })
    return () => {
      alive = false
    }
    // 최초 마운트 1회만 발송
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 인증번호 6자리 입력되면 '다음으로' 없이 자동으로 검증.
  // 같은 코드로는 1회만 발화(검증 성공/실패 후 verifying 해제 시 재발화로
  // 이미 소모된 코드를 다시 보내 '만료' 뜨는 것 방지). 코드가 바뀌면 다시 발화.
  const lastTriedRef = useRef('')
  useEffect(() => {
    if (isValid && !verifying && lastTriedRef.current !== code) {
      lastTriedRef.current = code
      handleVerified()
    }
  }, [isValid, verifying, code, handleVerified])

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown(cooldown - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])
  const cooldownText = `${Math.floor(cooldown / 60)}:${String(
    cooldown % 60
  ).padStart(2, '0')}`

  const handleNext = () => {
    if (!isValid) {
      setError('인증번호 6자리를 입력해주세요')
      return
    }
    handleVerified()
  }

  const handleResend = async () => {
    if (cooldown > 0) return
    try {
      await authApi.sendOtp({ phone })
      toastUtil.info('인증번호를 재전송했어요')
      setUnsubscribed(null)
      setCode('')
      setError('')
      lastTriedRef.current = ''
      setCooldown(60)
    } catch (e: any) {
      const notice = getUnsubscribedNotice(e)
      if (notice) {
        setUnsubscribed(notice)
        return
      }
      toastUtil.error(
        e?.response?.data?.message || '재전송에 실패했습니다'
      )
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        {/* 뒤로가기 (앱 표준 AppBar, 타이틀 없음) */}
        <AppBar onBack={() => navigate(-1)} />

        <div className="flex-1 overflow-y-auto scrollbar-hide px-20px pt-40px">
          <p className="font-pretendard font-bold text-22px leading-135% text-brown-80%-main">
            인증번호를 입력해주세요
          </p>
          {phoneFmt && (
            <p
              className="mt-8px font-pretendard font-regular text-15px leading-135%"
              style={{ color: 'rgba(75, 54, 33, 0.4)' }}
            >
              {phoneFmt}로 보냈어요
            </p>
          )}

          {/* 밑줄형 코드 입력 */}
          <div className="mt-32px">
            <input
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                if (error) setError('')
              }}
              inputMode="numeric"
              type="tel"
              autoComplete="one-time-code"
              className="w-full bg-transparent border-none outline-none font-pretendard font-bold text-22px leading-135% py-8px"
              style={{
                color: error ? ERROR_COLOR : 'rgba(75, 54, 33, 0.8)',
              }}
            />
            <div
              className="h-2px w-full"
              style={{
                backgroundColor: error ? ERROR_COLOR : 'rgba(75, 54, 33, 0.2)',
              }}
            />

            {error && (
              <div className="flex items-center gap-8px mt-12px">
                <img
                  src="/images/signup-x.png"
                  alt=""
                  className="w-20px h-20px object-contain"
                />
                <span
                  className="font-pretendard font-bold text-15px"
                  style={{ color: ERROR_COLOR }}
                >
                  {error}
                </span>
              </div>
            )}
          </div>

          {/* 수신거부 번호 안내 — 링크는 새 창(앱에선 외부/인앱 브라우저)으로 연다 */}
          {unsubscribed && (
            <div
              className="mt-20px rounded-12px px-16px py-14px"
              style={{ backgroundColor: 'rgba(180, 5, 8, 0.06)' }}
            >
              <p
                className="font-pretendard font-bold text-14px leading-135%"
                style={{ color: ERROR_COLOR }}
              >
                {unsubscribed.message}
                <a
                  href={unsubscribed.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-6px underline text-textColorText3"
                >
                  수신거부 해제하러 가기
                </a>
              </p>
            </div>
          )}

          {/* 재전송 (누르면 60초 쿨다운 타이머) */}
          <button
            type="button"
            onClick={handleResend}
            disabled={cooldown > 0}
            className="mt-20px flex items-center gap-6px"
          >
            <span
              className={`font-pretendard font-bold text-14px underline ${
                cooldown > 0 ? 'text-472410-20%' : 'text-textColorText3'
              }`}
            >
              재전송
            </span>
            {cooldown > 0 && (
              <span className="font-pretendard font-regular text-14px text-textColorText3">
                {cooldownText}
              </span>
            )}
          </button>
        </div>

        {/* 하단 다음으로 */}
        <div className="w-full px-20px">
          <SubmitButton
            text="다음으로"
            onSubmit={handleNext}
            onDisabled={handleNext}
          />
        </div>
      </div>
    </div>
  )
}

export default PhoneVerify
